import { useCallback } from 'react';
import { usePortalAuth } from '@/core/auth/hooks/usePortalAuth';
import { useApprovalData } from './useApprovalData';

interface UseApprovalViewOptions {
  clientId: string;
  mediaStage?: string;
  contentType?: 'fotos' | 'reels' | 'stories';
  shouldFetchInitialData?: boolean;
}

export const useApprovalView = ({ clientId, mediaStage, contentType, shouldFetchInitialData = true }: UseApprovalViewOptions) => {
  const auth = usePortalAuth({ clientId });
  const data = useApprovalData({
    clientId,
    token: auth.token ?? undefined,
    stage: mediaStage,
    contentType,
    autoFetch: shouldFetchInitialData,
  });

  // Destructure stable callbacks from data for useCallback dependencies
  const { fetchPosts, fetchMedia } = data;

  // Fetch data based on active tab when authenticated
  const fetchDataForTab = useCallback((tab: 'posts' | 'foto' | 'reel') => {
    if (!auth.isAuthenticated || auth.isCheckingSession) {
      return;
    }

    if (tab === 'posts') {
      fetchPosts();
    } else if (tab === 'foto' || tab === 'reel') {
      fetchMedia();
    }
  }, [auth.isAuthenticated, auth.isCheckingSession, fetchPosts, fetchMedia]);

  const handlePostUpdate = async (postId: string, updates: { caption?: string; status?: string }) => {
    try {
      await data.updatePost(postId, updates);
    } catch (err) {
      // Handle error - could emit to parent or show toast
      console.error('Failed to update post:', err);
    }
  };

  const handleReviewPost = async (postId: string, isReviewed: boolean) => {
    try {
      await data.reviewPost(postId, isReviewed);
    } catch (err) {
      console.error('Failed to update post review:', err);
      throw err;
    }
  };

  return {
    // Auth state
    isAuthenticated: auth.isAuthenticated,
    isValidating: auth.isValidating,
    isCheckingSession: auth.isCheckingSession,
    token: auth.token ?? '',
    role: auth.role ?? '',

    // Data state
    approvalData: data.approvalData,
    pendingMedia: data.pendingMedia,
    isLoading: data.isLoading,
    isInitialLoading: data.isInitialLoading,
    error: data.error,
    pendingPosts: data.pendingPosts,
    approvedPosts: data.approvedPosts,

    // Actions
    handlePostUpdate,
    handleReviewPost,
    rateMedia: data.rateMedia,
    updateMediaLabel: data.updateMediaLabel,
    fetchDataForTab,
    loadMoreMedia: data.loadMoreMedia,
    logout: auth.logout,

    // Pagination
    hasNext: data.hasNext,
    totalCount: data.totalCount,
  };
};